import React, { useState, useContext } from 'react';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';

const JuliaProfile = () => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const { sendRequestWithToast } = useContext(HttpRequestContext);

  const competencies = [
    { id: 'brain', text: "J'apprends tout ce qu'il y a à savoir sur votre entreprise" },
    { id: 'hourglass', text: "Je vous aide sur les tâches répétitives et chronophages" },
    { id: 'lightbulb', text: "Je fais des suggestions, des recos, des reportings" }
  ];

  const skills = [
    'Marketing', 'Opérations', 'RH', 'Comptabilité', 'Ventes',
    'Customer support', 'Finances', 'Advertising'
  ];

  const experiences = [
    {
      company: 'Leonidas',
      logo: '/images/leonidas_logo.png',
      tasks: [
        'Réponses aux commentaires des réseaux sociaux et analyse de sentiment des clients',
        'Gestion et dispatching des emails de la boîte général',
        'Assistance au customer support',
        'Création de reporting pour le marketing et la vente'
      ]
    },
    {
      company: 'CORICA',
      logo: '/images/corica-logo-baseline-black.jpg',
      tasks: [
        "Création de campagne d'emailing promotionnels et newsletter",
        'Création de contenu SEO pour le blog',
        'Gestion de la comptabilité et envoi des factures au comptable'
      ]
    },
    {
      company: 'LEROY MERLIN',
      logo: '/images/Leroy_Merlin.svg.webp',
      tasks: [
        'Assistance au client pour trouver des produits en ligne ou en boutique',
        'Gestion des tickets relatifs au SAV'
      ]
    },
    {
      company: 'MISTER CLÔTURE',
      logo: '/images/Mister Cloture 2-01.webp',
      tasks: [
        'Réalisation de devis sur mesure en ligne et réponses aux questions des clients',
        'Gestion du planning des équipes et planification des chantiers'
      ]
    }
  ];

  const salaryRequirements = [
    'Abonnement apd de 120€/mois (frais de setup éventuels)',
    'Pas de tickets resto',
    'Pas d\'éco chèque',
    'Pas de voiture de société',
    'Pas de congés'
  ];

  const handleHireClick = () => {
    setShowContactModal(true);
  };

  const handleLinkedInClick = () => {
    window.open('https://www.linkedin.com/in/julia-van-turing-a70b05367/', '_blank');
  };

  const handleContactClick = () => {
    setShowContactModal(true);
  };

  const handleCloseModal = () => {
    setShowContactModal(false);
    setContactForm({ name: '', email: '', phone: '' });
  };

  const handleFormChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare the data with additional context for Julia contact
    const submissionData = {
      ...contactForm,
      source: 'julia-ai-agent',
      service: 'ai-agent-julia',
      projectDescription: 'Demande de contact via la page Julia AI Agent'
    };
    
    try {
      await sendRequestWithToast({
        apiPath: '/form',
        type: 'POST',
        request: submissionData,
        successMessage: "Merci pour votre demande ! Julia vous contactera bientôt.",
        errorMessage: "Une erreur est survenue lors de l'envoi du formulaire."
      });
      
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting contact form:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-5xl mx-auto px-6 py-12 relative">
          {/* Top Header - Julia name and LinkedIn button */}
          <div className="flex justify-between items-center">
            <h1 className="text-4xl font-bold text-gray-900">Julia</h1>
            <button 
              onClick={handleLinkedInClick}
              className="flex items-center space-x-2 text-blue-600 border-2 border-blue-600 px-4 py-2 rounded-full hover:bg-blue-50 transition-colors font-medium"
            >
              <i className="fab fa-linkedin"></i>
              <span>Profil LinkedIn</span>
            </button>
          </div>

          {/* Main Hero Content */}
          <div className="relative flex flex-col items-center text-center">
            {/* Background JULIA text */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
              <img 
                src="/images/julia_back_text.png" 
                alt="Julia background text"
                className="w-full max-w-3xl h-auto opacity-15"
              />
            </div>

            {/* Profile Image */}
            <div className="relative z-10 mb-8">
              <img 
                src="/images/julia_image.png" 
                alt="Julia"
                className="w-64 h-auto object-contain mx-auto"
              />
            </div>

            {/* Tagline */}
            <p className="text-2xl text-gray-800 mb-8 font-light relative z-10">
              L'employée du mois, tous les mois.
            </p>

            {/* CTA Button */}
            <button 
              onClick={handleHireClick}
              className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors relative z-10"
            >
              M'engager dans votre entreprise
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16 space-y-20">
        {/* Competencies */}
        <section className="bg-white rounded-2xl p-12 shadow-lg">
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center tracking-wide">
            COMPÉTENCES
          </h2>
          
          {/* Core Competencies */}
          <div className="grid md:grid-cols-1 gap-8 mb-16">
            {competencies.map((competency, index) => (
              <div key={index} className="flex items-center space-x-8 p-6 bg-gradient-to-r from-orange-50 to-transparent rounded-xl hover:from-orange-100 transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                  {competency.id === 'brain' && <i className="fas fa-brain text-white text-2xl"></i>}
                  {competency.id === 'hourglass' && <i className="fas fa-hourglass-half text-white text-2xl"></i>}
                  {competency.id === 'lightbulb' && <i className="fas fa-lightbulb text-white text-2xl"></i>}
                </div>
                <p className="text-xl text-gray-800 leading-relaxed font-medium">{competency.text}</p>
              </div>
            ))}
          </div>
          
          {/* Skills Section */}
          <div className="border-t border-gray-200 pt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Domaines d'expertise
            </h3>
                         <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
               {skills.map((skill, index) => (
                 <div 
                   key={index}
                   className="group relative bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-4 text-center hover:from-orange-50 hover:to-orange-100 hover:border-orange-200 transition-all duration-300 cursor-default"
                 >
                   <span className="text-gray-700 group-hover:text-orange-700 font-semibold text-sm transition-colors">
                     {skill}
                   </span>
                   <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 opacity-0 group-hover:opacity-5 rounded-xl transition-opacity duration-300"></div>
                 </div>
               ))}
               
               {/* "And so much more" item that spans full width */}
               <div className="col-span-2 md:col-span-4 lg:col-span-4 group relative bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-200 rounded-xl p-6 text-center hover:from-orange-100 hover:to-orange-150 hover:border-orange-300 transition-all duration-300 cursor-default">
                 <span className="text-orange-700 group-hover:text-orange-800 font-bold text-lg transition-colors">
                   ... et bien plus encore !
                 </span>
                 <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 opacity-0 group-hover:opacity-10 rounded-xl transition-opacity duration-300"></div>
               </div>
             </div>
          </div>
        </section>

        {/* Professional Experience */}
        <section>
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center tracking-wide">
            EXPÉRIENCE PRO
          </h2>
          <div className="grid gap-10">
            {experiences.map((exp, index) => (
              <div key={index} className="bg-white rounded-2xl p-10 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="flex items-center space-x-8 mb-8">
                  <div className="flex-shrink-0">
                    <img 
                      src={exp.logo} 
                      alt={exp.company}
                      className="w-20 h-20 object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">{exp.company}</h3>
                    <div className="h-1 w-16 bg-gradient-to-r from-orange-400 to-orange-600 rounded"></div>
                  </div>
                </div>
                <div className="ml-28">
                  <ul className="space-y-4">
                    {exp.tasks.map((task, taskIndex) => (
                      <li key={taskIndex} className="text-gray-700 flex items-start text-lg leading-relaxed">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-4 mt-3 flex-shrink-0"></div>
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
            
            {/* Summary card */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 text-center border-2 border-orange-200">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                  <i className="fas fa-plus text-white text-xl"></i>
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-800 mb-2">+ 21 autres expériences</p>
              <p className="text-orange-700 text-lg">dans des secteurs variés et innovants</p>
            </div>
          </div>
        </section>

        {/* Info Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Salary Requirements */}
          <section className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-euro-sign text-green-600 text-2xl"></i>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              EXIGENCE SALARIALE
            </h3>
            <ul className="space-y-3">
              {salaryRequirements.map((requirement, index) => (
                <li key={index} className="text-gray-700 flex items-start">
                  <span className="text-green-500 mr-3 mt-1">•</span>
                  {requirement}
                </li>
              ))}
            </ul>
          </section>

          {/* Languages */}
          <section className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="fas fa-language text-purple-600 text-2xl"></i>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              LANGUES
            </h3>
            <p className="text-gray-700 text-center leading-relaxed">
              Je ne parle que 25 langues couramment mais j'apprends le finnois en ce moment. Hyvää päivää !
            </p>
          </section>

          {/* Interests */}
          <section className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <i className="fas fa-heart text-red-600 text-2xl"></i>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              CENTRE D'INTÉRÊT
            </h3>
            <p className="text-gray-700 text-center leading-relaxed">
              Je suis passionnée par la législation européenne. C'est simple : 
              je sais tout sur les politiques de confidentialité, l'utilisation des 
              données, le GDPR... c'est fascinant.
            </p>
          </section>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-700 rounded-3xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-6">Prête à rejoindre votre équipe !</h2>
          <p className="text-xl mb-8 text-gray-200">
            Transformez votre entreprise avec Julia, l'employée AI qui ne prend jamais de congés.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button 
              onClick={handleHireClick}
              className="bg-white text-gray-900 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              M'engager dans votre entreprise
            </button>
            <button 
              onClick={handleContactClick}
              className="flex items-center justify-center space-x-3 text-white border-2 border-white px-8 py-4 rounded-full hover:bg-white hover:text-gray-900 transition-colors font-semibold"
            >
              <i className="fas fa-envelope text-xl"></i>
              <span>Contactez-moi</span>
            </button>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Contactez Julia</h3>
              <button 
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center space-x-4 mb-4">
                <img 
                  src="/images/julia_image.png" 
                  alt="Julia"
                  className="w-16 h-16 object-contain"
                />
                <div>
                  <p className="text-gray-700">
                    Remplissez ce formulaire et je vous contacterai rapidement pour discuter de votre projet !
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={contactForm.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Votre nom et prénom"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email professionnel *
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="votre.email@entreprise.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de téléphone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  required
                  value={contactForm.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+32 xxx xx xx xx"
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  Envoyer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JuliaProfile; 